/* eslint-disable react/forbid-prop-types, react/jsx-no-bind */
import React, {useCallback, useEffect, useState} from 'react';
import PropTypes from 'prop-types';

import api from './ikoos-api';
import styles from './ikoos-app.css';

const getHashProjectId = () => {
    if (typeof window === 'undefined') return null;
    const match = window.location.hash.match(/#(\d+)/);
    return match ? match[1] : null;
};

const updateHashProjectId = projectId => {
    if (typeof window === 'undefined' || !projectId || projectId === '0') return;
    const nextUrl = `${window.location.pathname}${window.location.search}#${projectId}`;
    window.history.replaceState('project', 'project', nextUrl);
};

const buildProjectShareUrl = projectId => {
    if (typeof window === 'undefined') return `player.html#${projectId}`;
    const playerPath = window.location.pathname.replace(/[^/]*$/, 'player.html') || '/player.html';
    return `${window.location.origin}${playerPath}#${projectId}`;
};

const getResetPasswordParams = () => {
    if (typeof window === 'undefined') return null;
    const searchParams = new URLSearchParams(window.location.search);
    const token = searchParams.get('resetToken') || searchParams.get('reset_token') || searchParams.get('token');
    if (!token) return null;
    return {
        email: searchParams.get('email') || '',
        token
    };
};

const clearResetPasswordParams = () => {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    ['resetToken', 'reset_token', 'token', 'email'].forEach(key => url.searchParams.delete(key));
    window.history.replaceState('reset-password', 'reset-password', `${url.pathname}${url.search}${url.hash}`);
};

const withTimeout = (promise, timeout, message) => Promise.race([
    promise,
    new Promise((resolve, reject) => {
        setTimeout(() => reject(new Error(message)), timeout);
    })
]);

const copyText = text => {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
        return withTimeout(navigator.clipboard.writeText(text), 1500, 'Clipboard timeout.');
    }
    return Promise.reject(new Error('Clipboard tidak tersedia.'));
};

const readFileAsDataUrl = file => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('File avatar tidak bisa dibaca.'));
    reader.readAsDataURL(file);
});

const formatDate = value => {
    if (!value) return '';
    try {
        return new Intl.DateTimeFormat('id-ID', {
            dateStyle: 'medium',
            timeStyle: 'short'
        }).format(new Date(value));
    } catch {
        return value;
    }
};

const TextField = ({label, name, type = 'text', value, onChange, autoComplete}) => (
    <label className={styles.field}>
        {label}
        <input
            className={styles.input}
            name={name}
            type={type}
            value={value}
            autoComplete={autoComplete}
            onChange={onChange}
        />
    </label>
);

TextField.propTypes = {
    autoComplete: PropTypes.string,
    label: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    type: PropTypes.string,
    value: PropTypes.string.isRequired
};

const Modal = ({children, title, wide, onClose}) => (
    <div
        className={styles.modalBackdrop}
        role="presentation"
        onMouseDown={event => {
            if (event.target === event.currentTarget) onClose();
        }}
    >
        <section
            aria-modal="true"
            className={wide ? `${styles.modal} ${styles.modalWide}` : styles.modal}
            role="dialog"
        >
            <h2 className={styles.modalTitle}>{title}</h2>
            {children}
        </section>
    </div>
);

Modal.propTypes = {
    children: PropTypes.node,
    onClose: PropTypes.func.isRequired,
    title: PropTypes.string.isRequired,
    wide: PropTypes.bool
};

const IkoosApp = ({Gui, guiProps = {}}) => {
    const [user, setUser] = useState(null);
    const [sessionLoading, setSessionLoading] = useState(true);
    const [currentProjectId, setCurrentProjectId] = useState(getHashProjectId());
    const [authModal, setAuthModal] = useState(null);
    const [loginForm, setLoginForm] = useState({identity: '', password: ''});
    const [loginError, setLoginError] = useState('');
    const [loginLoading, setLoginLoading] = useState(false);
    const [registerForm, setRegisterForm] = useState({username: '', email: '', password: ''});
    const [registerError, setRegisterError] = useState('');
    const [registerLoading, setRegisterLoading] = useState(false);
    const [forgotForm, setForgotForm] = useState({email: '', token: '', password: ''});
    const [forgotMessage, setForgotMessage] = useState('');
    const [forgotError, setForgotError] = useState('');
    const [forgotLoading, setForgotLoading] = useState(false);
    const [resetLoading, setResetLoading] = useState(false);
    const [projectsOpen, setProjectsOpen] = useState(false);
    const [projects, setProjects] = useState([]);
    const [projectsLoading, setProjectsLoading] = useState(false);
    const [projectsError, setProjectsError] = useState('');
    const [projectsMessage, setProjectsMessage] = useState('');
    const [projectActionId, setProjectActionId] = useState(null);
    const [profileOpen, setProfileOpen] = useState(false);
    const [profileForm, setProfileForm] = useState({displayName: '', username: '', email: ''});
    const [profileAvatarDataUrl, setProfileAvatarDataUrl] = useState('');
    const [profileAvatarPreview, setProfileAvatarPreview] = useState('');
    const [profileError, setProfileError] = useState('');
    const [profileMessage, setProfileMessage] = useState('');
    const [profileLoading, setProfileLoading] = useState(false);
    const [passwordOpen, setPasswordOpen] = useState(false);
    const [passwordForm, setPasswordForm] = useState({
        currentPassword: '',
        password: '',
        confirmPassword: ''
    });
    const [passwordError, setPasswordError] = useState('');
    const [passwordMessage, setPasswordMessage] = useState('');
    const [passwordLoading, setPasswordLoading] = useState(false);

    const loadProjects = useCallback(() => {
        if (!user) return Promise.resolve();
        setProjectsLoading(true);
        setProjectsError('');
        return api.listProjects()
            .then(payload => {
                setProjects(payload.projects || []);
            })
            .catch(error => {
                setProjectsError(error.message);
            })
            .finally(() => {
                setProjectsLoading(false);
            });
    }, [user]);

    useEffect(() => {
        api.me()
            .then(payload => {
                setUser(payload.user || null);
            })
            .catch(() => {
                setUser(null);
            })
            .finally(() => {
                setSessionLoading(false);
            });
    }, []);

    useEffect(() => {
        const syncProjectIdFromHash = () => setCurrentProjectId(getHashProjectId());

        if (typeof window === 'undefined') return () => {};
        window.addEventListener('hashchange', syncProjectIdFromHash);
        syncProjectIdFromHash();
        return () => window.removeEventListener('hashchange', syncProjectIdFromHash);
    }, []);

    useEffect(() => {
        const resetParams = getResetPasswordParams();
        if (!resetParams) return;
        setAuthModal('forgot');
        setForgotForm(current => ({
            ...current,
            email: resetParams.email || current.email,
            token: resetParams.token
        }));
        setForgotMessage('Token reset dari email sudah terisi. Masukkan password baru.');
        clearResetPasswordParams();
    }, []);

    useEffect(() => {
        if (!user) return;
        setProfileForm({
            displayName: user.displayName || user.username || '',
            username: user.username || '',
            email: user.email || ''
        });
        setProfileAvatarPreview(user.avatarUrl || '');
        setProfileAvatarDataUrl('');
    }, [user]);

    useEffect(() => {
        if (user && projectsOpen) loadProjects();
    }, [user, projectsOpen, loadProjects]);

    const handleLoginChange = event => {
        setLoginForm({
            ...loginForm,
            [event.target.name]: event.target.value
        });
    };

    const handleRegisterChange = event => {
        setRegisterForm({
            ...registerForm,
            [event.target.name]: event.target.value
        });
    };

    const handleForgotChange = event => {
        setForgotForm({
            ...forgotForm,
            [event.target.name]: event.target.value
        });
    };

    const handleProfileChange = event => {
        setProfileForm({
            ...profileForm,
            [event.target.name]: event.target.value
        });
    };

    const handlePasswordChange = event => {
        setPasswordForm({
            ...passwordForm,
            [event.target.name]: event.target.value
        });
    };

    const handleAvatarChange = event => {
        const file = event.target.files && event.target.files[0];
        if (!file) return;

        setProfileError('');
        setProfileMessage('');

        if (!/^image\/(png|jpeg|jpg|gif|webp)$/.test(file.type)) {
            setProfileError('Avatar harus berupa gambar PNG, JPG, GIF, atau WebP.');
            return;
        }
        if (file.size > 2 * 1024 * 1024) {
            setProfileError('Ukuran avatar maksimal 2MB.');
            return;
        }

        readFileAsDataUrl(file)
            .then(dataUrl => {
                setProfileAvatarDataUrl(dataUrl);
                setProfileAvatarPreview(dataUrl);
            })
            .catch(error => {
                setProfileError(error.message);
            });
    };

    const handleProfileSubmit = event => {
        event.preventDefault();
        setProfileLoading(true);
        setProfileError('');
        setProfileMessage('');

        api.updateProfile(profileForm)
            .then(payload => {
                setUser(payload.user);
                if (!profileAvatarDataUrl) return payload;
                return api.uploadAvatar({avatarDataUrl: profileAvatarDataUrl});
            })
            .then(payload => {
                setUser(payload.user);
                setProfileAvatarDataUrl('');
                setProfileAvatarPreview(payload.user.avatarUrl || '');
                setProfileMessage('Profile berhasil diperbarui.');
            })
            .catch(error => {
                setProfileError(error.message);
            })
            .finally(() => {
                setProfileLoading(false);
            });
    };

    const handlePasswordSubmit = event => {
        event.preventDefault();
        setPasswordError('');
        setPasswordMessage('');

        if (passwordForm.password !== passwordForm.confirmPassword) {
            setPasswordError('Konfirmasi password baru belum sama.');
            return;
        }

        setPasswordLoading(true);
        api.changePassword({
            currentPassword: passwordForm.currentPassword,
            password: passwordForm.password
        })
            .then(() => {
                setPasswordForm({
                    currentPassword: '',
                    password: '',
                    confirmPassword: ''
                });
                setPasswordMessage('Password berhasil diperbarui.');
            })
            .catch(error => {
                setPasswordError(error.message);
            })
            .finally(() => {
                setPasswordLoading(false);
            });
    };

    const handleLogin = onClose => event => {
        event.preventDefault();
        setLoginLoading(true);
        setLoginError('');

        api.login(loginForm)
            .then(payload => {
                setUser(payload.user);
                setLoginForm({identity: '', password: ''});
                if (onClose) onClose();
            })
            .catch(error => {
                setLoginError(error.message);
            })
            .finally(() => {
                setLoginLoading(false);
            });
    };

    const handleRegister = event => {
        event.preventDefault();
        setRegisterLoading(true);
        setRegisterError('');

        api.register(registerForm)
            .then(payload => {
                setUser(payload.user);
                setRegisterForm({username: '', email: '', password: ''});
                setAuthModal(null);
            })
            .catch(error => {
                setRegisterError(error.message);
            })
            .finally(() => {
                setRegisterLoading(false);
            });
    };

    const handleForgotPassword = event => {
        event.preventDefault();
        setForgotLoading(true);
        setForgotError('');
        setForgotMessage('');

        api.forgotPassword({email: forgotForm.email})
            .then(payload => {
                const message = payload.resetToken ?
                    `${payload.message} Token reset: ${payload.resetToken}` :
                    payload.message;
                setForgotMessage(message);
                if (payload.resetToken) {
                    setForgotForm(current => ({
                        ...current,
                        token: payload.resetToken
                    }));
                }
            })
            .catch(error => {
                setForgotError(error.message);
            })
            .finally(() => {
                setForgotLoading(false);
            });
    };

    const handleResetPassword = event => {
        event.preventDefault();
        setResetLoading(true);
        setForgotError('');

        api.resetPassword({
            token: forgotForm.token,
            password: forgotForm.password
        })
            .then(() => {
                setForgotMessage('Password berhasil diperbarui. Silakan login kembali.');
                setForgotForm({email: '', token: '', password: ''});
                setAuthModal(null);
            })
            .catch(error => {
                setForgotError(error.message);
            })
            .finally(() => {
                setResetLoading(false);
            });
    };

    const handleLogout = () => {
        api.logout()
            .finally(() => {
                setUser(null);
                setProjects([]);
                setProjectsOpen(false);
                setProfileOpen(false);
                setPasswordOpen(false);
            });
    };

    const handleOpenProjects = () => {
        setProjectsError('');
        setProjectsMessage('');
        setProjectsOpen(true);
        loadProjects();
    };

    const handleOpenProject = projectId => {
        setProjectsOpen(false);
        setCurrentProjectId(projectId);
        window.location.hash = projectId;
    };

    const handleDeleteProject = project => {
        const title = project.title || 'project ini';
        let shouldDelete = true;
        if (typeof window !== 'undefined') {
            // eslint-disable-next-line no-alert
            shouldDelete = window.confirm(`Hapus "${title}"? Project yang sudah dihapus tidak bisa dikembalikan.`);
        }
        if (!shouldDelete) {
            return;
        }

        setProjectActionId(project.id);
        setProjectsError('');
        setProjectsMessage('');
        api.deleteProject(project.id)
            .then(() => {
                if (currentProjectId === project.id) {
                    setCurrentProjectId(null);
                    if (typeof window !== 'undefined') window.location.hash = '0';
                }
                setProjectsMessage('Project berhasil dihapus.');
                return loadProjects();
            })
            .catch(error => {
                setProjectsError(error.message);
            })
            .finally(() => {
                setProjectActionId(null);
            });
    };

    const handleShareProject = project => {
        const shareUrl = buildProjectShareUrl(project.id);

        setProjectActionId(project.id);
        setProjectsError('');
        setProjectsMessage('');

        api.shareProject(project.id)
            .then(() => {
                const sharePayload = {
                    title: project.title || 'Project IKOOS',
                    text: project.title || 'Project IKOOS',
                    url: shareUrl
                };
                if (typeof navigator !== 'undefined' && navigator.share) {
                    return withTimeout(navigator.share(sharePayload), 1500, 'Share timeout.')
                        .then(() => 'shared')
                        .catch(() => copyText(shareUrl)
                            .then(() => 'copied')
                            .catch(() => 'ready'));
                }
                return copyText(shareUrl)
                    .then(() => 'copied')
                    .catch(() => 'ready');
            })
            .then(mode => {
                if (mode === 'shared') {
                    setProjectsMessage('Link preview project berhasil dibagikan.');
                } else if (mode === 'copied') {
                    setProjectsMessage(`Link preview project berhasil disalin: ${shareUrl}`);
                } else {
                    setProjectsMessage(`Link preview project siap dibagikan: ${shareUrl}`);
                }
                return loadProjects();
            })
            .catch(error => {
                setProjectsError(error.message);
            })
            .finally(() => {
                setProjectActionId(null);
            });
    };

    const handleProjectIdUpdate = useCallback(projectId => {
        const nextProjectId = projectId && projectId !== '0' ? projectId.toString() : null;
        setCurrentProjectId(nextProjectId);
        if (nextProjectId) updateHashProjectId(nextProjectId);
        if (user) loadProjects();
    }, [user, loadProjects]);

    const saveProjectData = useCallback((projectId, vmState, params) => api
        .saveProject(projectId, vmState, params)
        .then(response => {
            if (user) loadProjects();
            return response;
        }), [user, loadProjects]);

    const saveProjectThumbnail = useCallback((projectId, thumbnail) => api
        .saveProjectThumbnail(projectId, thumbnail)
        .then(() => {
            if (user) loadProjects();
        }), [user, loadProjects]);

    const renderLogin = ({onClose}) => (
        <form
            className={styles.authForm}
            onClick={event => event.stopPropagation()}
            onSubmit={handleLogin(onClose)}
        >
            <TextField
                autoComplete="username"
                label="Username atau email"
                name="identity"
                value={loginForm.identity}
                onChange={handleLoginChange}
            />
            <TextField
                autoComplete="current-password"
                label="Password"
                name="password"
                type="password"
                value={loginForm.password}
                onChange={handleLoginChange}
            />
            {loginError ? <div className={styles.error}>{loginError}</div> : null}
            <button
                className={styles.primaryButton}
                disabled={loginLoading}
                type="submit"
            >
                {loginLoading ? 'Memproses...' : 'Masuk'}
            </button>
            <button
                className={styles.linkButton}
                type="button"
                onClick={() => {
                    if (onClose) onClose();
                    setAuthModal('forgot');
                }}
            >
                Lupa password?
            </button>
            <button
                className={styles.linkButton}
                type="button"
                onClick={() => {
                    if (onClose) onClose();
                    setAuthModal('register');
                }}
            >
                Daftar akun baru
            </button>
        </form>
    );

    const loggedIn = Boolean(user);
    const canUpdateExistingProject = loggedIn && Boolean(currentProjectId);

    return (
        <React.Fragment>
            <Gui
                {...guiProps}
                accountMenuOptions={{
                    canHaveSession: true,
                    canRegister: !loggedIn,
                    canLogin: !loggedIn,
                    canLogout: loggedIn,
                    avatarUrl: user ? user.avatarUrl : null
                }}
                assetHost={api.assetHost}
                autoCreateNewProjects={false}
                canCreateNew={loggedIn}
                canSave={canUpdateExistingProject}
                isFetchingUserData={sessionLoading}
                projectHost={api.projectHost}
                renderLogin={renderLogin}
                username={user ? (user.displayName || user.username) : ''}
                userOwnsProject={loggedIn}
                onLogOut={handleLogout}
                onOpenMyStuff={handleOpenProjects}
                onOpenPassword={() => {
                    setPasswordError('');
                    setPasswordMessage('');
                    setPasswordOpen(true);
                }}
                onOpenProfile={() => {
                    setProfileError('');
                    setProfileMessage('');
                    setProfileOpen(true);
                }}
                onOpenRegistration={() => setAuthModal('register')}
                onUpdateProjectData={saveProjectData}
                onUpdateProjectId={handleProjectIdUpdate}
                onUpdateProjectThumbnail={saveProjectThumbnail}
            />

            {authModal === 'register' ? (
                <Modal
                    title="Daftar Member IKOOS"
                    onClose={() => setAuthModal(null)}
                >
                    <form
                        className={styles.authForm}
                        onSubmit={handleRegister}
                    >
                        <TextField
                            autoComplete="username"
                            label="Username"
                            name="username"
                            value={registerForm.username}
                            onChange={handleRegisterChange}
                        />
                        <TextField
                            autoComplete="email"
                            label="Email"
                            name="email"
                            type="email"
                            value={registerForm.email}
                            onChange={handleRegisterChange}
                        />
                        <TextField
                            autoComplete="new-password"
                            label="Password"
                            name="password"
                            type="password"
                            value={registerForm.password}
                            onChange={handleRegisterChange}
                        />
                        {registerError ? <div className={styles.error}>{registerError}</div> : null}
                        <div className={styles.buttonRow}>
                            <button
                                className={styles.secondaryButton}
                                type="button"
                                onClick={() => setAuthModal(null)}
                            >
                                Batal
                            </button>
                            <button
                                className={styles.primaryButton}
                                disabled={registerLoading}
                                type="submit"
                            >
                                {registerLoading ? 'Memproses...' : 'Daftar'}
                            </button>
                        </div>
                    </form>
                </Modal>
            ) : null}

            {authModal === 'forgot' ? (
                <Modal
                    title="Lupa Password"
                    onClose={() => setAuthModal(null)}
                >
                    <form
                        className={styles.authForm}
                        onSubmit={handleForgotPassword}
                    >
                        <TextField
                            autoComplete="email"
                            label="Email"
                            name="email"
                            type="email"
                            value={forgotForm.email}
                            onChange={handleForgotChange}
                        />
                        <button
                            className={styles.primaryButton}
                            disabled={forgotLoading}
                            type="submit"
                        >
                            {forgotLoading ? 'Memproses...' : 'Kirim reset password'}
                        </button>
                    </form>
                    {forgotMessage ? (
                        <p className={forgotMessage.includes('Token reset') ? styles.resetToken : styles.success}>
                            {forgotMessage}
                        </p>
                    ) : null}
                    <form
                        className={styles.authForm}
                        onSubmit={handleResetPassword}
                    >
                        <TextField
                            autoComplete="one-time-code"
                            label="Token reset"
                            name="token"
                            value={forgotForm.token}
                            onChange={handleForgotChange}
                        />
                        <TextField
                            autoComplete="new-password"
                            label="Password baru"
                            name="password"
                            type="password"
                            value={forgotForm.password}
                            onChange={handleForgotChange}
                        />
                        {forgotError ? <div className={styles.error}>{forgotError}</div> : null}
                        <div className={styles.buttonRow}>
                            <button
                                className={styles.secondaryButton}
                                type="button"
                                onClick={() => setAuthModal(null)}
                            >
                                Tutup
                            </button>
                            <button
                                className={styles.primaryButton}
                                disabled={resetLoading}
                                type="submit"
                            >
                                {resetLoading ? 'Memproses...' : 'Ubah password'}
                            </button>
                        </div>
                    </form>
                </Modal>
            ) : null}

            {profileOpen ? (
                <Modal
                    title="Profile Peserta"
                    onClose={() => setProfileOpen(false)}
                >
                    <form
                        className={styles.authForm}
                        onSubmit={handleProfileSubmit}
                    >
                        <div className={styles.avatarEditor}>
                            <div className={styles.avatarPreview}>
                                {profileAvatarPreview ? (
                                    <img
                                        alt=""
                                        src={profileAvatarPreview}
                                    />
                                ) : (
                                    <span>{(profileForm.displayName || profileForm.username || 'I').charAt(0)}</span>
                                )}
                            </div>
                            <label className={styles.avatarUploadButton}>
                                Upload foto/avatar
                                <input
                                    accept="image/png,image/jpeg,image/gif,image/webp"
                                    className={styles.fileInput}
                                    type="file"
                                    onChange={handleAvatarChange}
                                />
                            </label>
                        </div>
                        <TextField
                            autoComplete="name"
                            label="Nama peserta"
                            name="displayName"
                            value={profileForm.displayName}
                            onChange={handleProfileChange}
                        />
                        <TextField
                            autoComplete="username"
                            label="Username"
                            name="username"
                            value={profileForm.username}
                            onChange={handleProfileChange}
                        />
                        <TextField
                            autoComplete="email"
                            label="Email"
                            name="email"
                            type="email"
                            value={profileForm.email}
                            onChange={handleProfileChange}
                        />
                        {profileError ? <div className={styles.error}>{profileError}</div> : null}
                        {profileMessage ? <div className={styles.success}>{profileMessage}</div> : null}
                        <div className={styles.buttonRow}>
                            <button
                                className={styles.secondaryButton}
                                type="button"
                                onClick={() => setProfileOpen(false)}
                            >
                                Tutup
                            </button>
                            <button
                                className={styles.primaryButton}
                                disabled={profileLoading}
                                type="submit"
                            >
                                {profileLoading ? 'Menyimpan...' : 'Simpan profile'}
                            </button>
                        </div>
                    </form>
                </Modal>
            ) : null}

            {passwordOpen ? (
                <Modal
                    title="Ubah Password"
                    onClose={() => setPasswordOpen(false)}
                >
                    <form
                        className={styles.authForm}
                        onSubmit={handlePasswordSubmit}
                    >
                        <TextField
                            autoComplete="current-password"
                            label="Password lama"
                            name="currentPassword"
                            type="password"
                            value={passwordForm.currentPassword}
                            onChange={handlePasswordChange}
                        />
                        <TextField
                            autoComplete="new-password"
                            label="Password baru"
                            name="password"
                            type="password"
                            value={passwordForm.password}
                            onChange={handlePasswordChange}
                        />
                        <TextField
                            autoComplete="new-password"
                            label="Konfirmasi password baru"
                            name="confirmPassword"
                            type="password"
                            value={passwordForm.confirmPassword}
                            onChange={handlePasswordChange}
                        />
                        {passwordError ? <div className={styles.error}>{passwordError}</div> : null}
                        {passwordMessage ? <div className={styles.success}>{passwordMessage}</div> : null}
                        <div className={styles.buttonRow}>
                            <button
                                className={styles.secondaryButton}
                                type="button"
                                onClick={() => setPasswordOpen(false)}
                            >
                                Tutup
                            </button>
                            <button
                                className={styles.primaryButton}
                                disabled={passwordLoading}
                                type="submit"
                            >
                                {passwordLoading ? 'Memproses...' : 'Ubah password'}
                            </button>
                        </div>
                    </form>
                </Modal>
            ) : null}

            {projectsOpen ? (
                <Modal
                    wide
                    title="Project Saya"
                    onClose={() => setProjectsOpen(false)}
                >
                    {projectsLoading ? <p className={styles.empty}>Memuat project...</p> : null}
                    {projectsError ? <p className={styles.error}>{projectsError}</p> : null}
                    {projectsMessage ? <p className={styles.success}>{projectsMessage}</p> : null}
                    {!projectsLoading && !projectsError && projects.length === 0 ? (
                        <p className={styles.empty}>Belum ada project tersimpan.</p>
                    ) : null}
                    <div className={styles.projectGrid}>
                        {projects.map(project => {
                            const isBusy = projectActionId === project.id;
                            return (
                                <article
                                    className={styles.projectItem}
                                    key={project.id}
                                >
                                    <button
                                        className={styles.projectOpenButton}
                                        type="button"
                                        onClick={() => handleOpenProject(project.id)}
                                    >
                                        <span className={styles.projectThumb}>
                                            {project.thumbnailUrl ? (
                                                <img
                                                    alt=""
                                                    src={project.thumbnailUrl}
                                                />
                                            ) : 'IKOOS'}
                                        </span>
                                        <span className={styles.projectInfo}>
                                            <span className={styles.projectTitle}>{project.title}</span>
                                            <span className={styles.projectDate}>{formatDate(project.updatedAt)}</span>
                                            {project.shared ? (
                                                <span className={styles.projectBadge}>Dibagikan</span>
                                            ) : null}
                                        </span>
                                    </button>
                                    <div className={styles.projectActions}>
                                        <button
                                            className={styles.shareButton}
                                            disabled={isBusy}
                                            type="button"
                                            onClick={() => handleShareProject(project)}
                                        >
                                            Bagikan
                                        </button>
                                        <button
                                            className={styles.dangerButton}
                                            disabled={isBusy}
                                            type="button"
                                            onClick={() => handleDeleteProject(project)}
                                        >
                                            Hapus
                                        </button>
                                    </div>
                                </article>
                            );
                        })}
                    </div>
                    <div className={styles.buttonRow}>
                        <button
                            className={styles.secondaryButton}
                            type="button"
                            onClick={() => setProjectsOpen(false)}
                        >
                            Tutup
                        </button>
                    </div>
                </Modal>
            ) : null}
        </React.Fragment>
    );
};

IkoosApp.propTypes = {
    Gui: PropTypes.elementType.isRequired,
    guiProps: PropTypes.object
};

export default IkoosApp;
